import { useState } from 'react';
import { BarChart2, TrendingDown, Layers, Zap, Package, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMexicoDateString } from '@/lib/mexicoDate';

type KpiData = {
  kgPorMaquinaFlexo: { machine: string; kgTotal: number }[];
  cambiosPorMaquinaFlexo: { machine: string; cambios: number }[];
  kgPromedioArranque: number;
  kgExtruidos: number;
  kgLaminados: number;
  kgProducidosPorTipo: { frente: number; extruido: number; laminado: number; total: number };
  porcentajeDesperdicio: number;
  totalKgRegistrados: number;
  totalKgProductoTerminado: number;
  totalKgProceso: number;
  totalKgMerma: number;
  totalKgProductoTerminadoNeto: number;
  mermaPorMotivo: { motivo: string; kgTotal: number }[];
  eventsCount: number;
};

function fmt(n: number) {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n: number) {
  return n.toLocaleString('es-MX');
}

// Simple horizontal bar using CSS width %
function HBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-5 overflow-hidden">
      <div
        className={`h-5 rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// SVG circular gauge for %
function CircularGauge({ pct, color }: { pct: number; color: string }) {
  const r = 48;
  const circ = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const offset = circ * (1 - clamped / 100);
  return (
    <svg viewBox="0 0 120 120" className="w-36 h-36">
      <circle cx="60" cy="60" r={r} stroke="#e5e7eb" strokeWidth="14" fill="none" />
      <circle
        cx="60" cy="60" r={r}
        stroke={color}
        strokeWidth="14"
        fill="none"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 60 60)"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x="60" y="55" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111827">
        {fmt(clamped)}%
      </text>
      <text x="60" y="72" textAnchor="middle" fontSize="10" fill="#6b7280">desperdicio</text>
    </svg>
  );
}

// Stacked bar for KPI 6
function StackedBar({ frente, extruido, laminado }: { frente: number; extruido: number; laminado: number }) {
  const total = frente + extruido + laminado;
  if (total === 0) return <div className="text-xs text-gray-400 py-2">Sin datos de PT en el periodo</div>;
  const fPct = (frente / total) * 100;
  const ePct = (extruido / total) * 100;
  const lPct = (laminado / total) * 100;
  return (
    <div className="space-y-3">
      <div className="flex w-full h-6 rounded-lg overflow-hidden">
        {frente > 0 && <div className="bg-orange-400" style={{ width: `${fPct}%` }} title={`Frente: ${fmt(frente)} kg`} />}
        {extruido > 0 && <div className="bg-blue-400" style={{ width: `${ePct}%` }} title={`Extruido: ${fmt(extruido)} kg`} />}
        {laminado > 0 && <div className="bg-violet-400" style={{ width: `${lPct}%` }} title={`Laminado: ${fmt(laminado)} kg`} />}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-400 inline-block" />Frente {fmt(fPct)}%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-400 inline-block" />Extruido {fmt(ePct)}%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-violet-400 inline-block" />Laminado {fmt(lPct)}%</span>
      </div>
    </div>
  );
}

export default function IndicadoresView() {
  const today = getMexicoDateString();
  const firstOfMonth = `${today.slice(0, 7)}-01`;

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [customerName, setCustomerName] = useState('');
  const [productName, setProductName] = useState('');
  const [data, setData] = useState<KpiData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queried, setQueried] = useState(false);

  const token = () => localStorage.getItem('token');

  const handleConsultar = async () => {
    if (!from || !to) { setError('Selecciona un rango de fechas.'); return; }
    setError(null);
    setLoading(true);
    setQueried(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (customerName.trim()) params.set('customerName', customerName.trim());
      if (productName.trim()) params.set('productName', productName.trim());

      const res = await fetch(`/api/reports/indicadores?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `Error ${res.status} al obtener indicadores`);
      }
      setData(await res.json());
    } catch (err: any) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const maxKgFlexo = data ? Math.max(...data.kgPorMaquinaFlexo.map(r => r.kgTotal), 1) : 1;
  const maxCambios = data ? Math.max(...data.cambiosPorMaquinaFlexo.map(r => r.cambios), 1) : 1;
  const hasFlexoData = data && (data.kgPorMaquinaFlexo.length > 0 || data.cambiosPorMaquinaFlexo.length > 0);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <BarChart2 className="h-6 w-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Indicadores de Producción</h1>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cliente</label>
            <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Filtrar por cliente…"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-48" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Producto</label>
            <input type="text" value={productName} onChange={(e) => setProductName(e.target.value)}
              placeholder="Filtrar por producto…"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-48" />
          </div>
          <Button onClick={handleConsultar} disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white">
            {loading ? 'Consultando…' : 'Consultar'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 mb-4 flex gap-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <p>{error}</p>
            {error.includes('403') && (
              <p className="mt-1 text-xs">El permiso <code>reports:view</code> no está asignado a tu rol. Pide al administrador que lo asigne en Roles y Permisos, luego vuelve a iniciar sesión.</p>
            )}
          </div>
        </div>
      )}

      {!queried && !loading && (
        <div className="text-center text-gray-400 py-16">
          <BarChart2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Selecciona un rango de fechas y presiona <strong>Consultar</strong></p>
        </div>
      )}

      {queried && !loading && data && (
        <>
          {/* Diagnostic banner: events found but no weight data */}
          {data.eventsCount > 0 && data.totalKgRegistrados === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex gap-2 text-sm text-amber-800">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Se encontraron {fmtInt(data.eventsCount)} eventos pero sin registros de peso.</p>
                <p className="text-xs mt-1">Verifica que: los conceptos de peso estén configurados correctamente, y que el formato del peso en los eventos sea "Peso: X kg".</p>
              </div>
            </div>
          )}

          {data.eventsCount === 0 && (
            <div className="text-center text-gray-400 py-12">
              <BarChart2 className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="font-medium">Sin eventos en el periodo seleccionado.</p>
              <p className="text-sm mt-1">Cambia el rango de fechas o los filtros e intenta de nuevo.</p>
            </div>
          )}

          {data.eventsCount > 0 && data.totalKgRegistrados > 0 && (
            <div className="space-y-6">

              {/* Row 1: KPI 4, 5 + KPI 7 gauge */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* KPI 4 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="h-4 w-4 text-blue-500" />
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kg Extruidos</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{fmt(data.kgExtruidos)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">kg totales</p>
                  <div className="mt-3">
                    <HBar value={data.kgExtruidos} max={Math.max(data.kgExtruidos, data.kgLaminados, 1)} color="bg-blue-400" />
                  </div>
                </div>
                {/* KPI 5 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="h-4 w-4 text-violet-500" />
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kg Laminados</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{fmt(data.kgLaminados)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">kg totales</p>
                  <div className="mt-3">
                    <HBar value={data.kgLaminados} max={Math.max(data.kgExtruidos, data.kgLaminados, 1)} color="bg-violet-400" />
                  </div>
                </div>
                {/* KPI 3 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="h-4 w-4 text-amber-500" />
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kg Promedio Arranque (Flexo)</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{fmt(data.kgPromedioArranque)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">kg por cambio de trabajo</p>
                </div>
              </div>

              {/* Row 2: KPI 6 stacked + KPI 7 gauge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* KPI 6 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="h-5 w-5 text-green-600" />
                    <p className="text-sm font-semibold text-gray-700">KPI 6 — Kg Producidos por Tipo</p>
                  </div>
                  <StackedBar
                    frente={data.kgProducidosPorTipo.frente}
                    extruido={data.kgProducidosPorTipo.extruido}
                    laminado={data.kgProducidosPorTipo.laminado}
                  />
                  <table className="w-full text-sm mt-4">
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="py-1.5 flex items-center gap-2"><span className="w-3 h-3 rounded bg-orange-400 inline-block" />Frente (Flexo)</td>
                        <td className="py-1.5 text-right font-mono font-medium">{fmt(data.kgProducidosPorTipo.frente)} kg</td>
                      </tr>
                      <tr>
                        <td className="py-1.5 flex items-center gap-2"><span className="w-3 h-3 rounded bg-blue-400 inline-block" />Extruido</td>
                        <td className="py-1.5 text-right font-mono font-medium">{fmt(data.kgProducidosPorTipo.extruido)} kg</td>
                      </tr>
                      <tr>
                        <td className="py-1.5 flex items-center gap-2"><span className="w-3 h-3 rounded bg-violet-400 inline-block" />Laminado</td>
                        <td className="py-1.5 text-right font-mono font-medium">{fmt(data.kgProducidosPorTipo.laminado)} kg</td>
                      </tr>
                      <tr className="border-t-2 border-gray-200">
                        <td className="py-1.5 font-semibold text-gray-800">Total PT</td>
                        <td className="py-1.5 text-right font-mono font-bold">{fmt(data.kgProducidosPorTipo.total)} kg</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* KPI 7 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingDown className="h-5 w-5 text-red-500" />
                    <p className="text-sm font-semibold text-gray-700">KPI 7 — % Desperdicio del Proceso</p>
                  </div>
                  <div className="flex flex-col items-center gap-3">
                    <CircularGauge pct={data.porcentajeDesperdicio} color="#ef4444" />
                    <div className="text-center text-sm text-gray-500 space-y-1">
                      <p>Producto en Proceso: <span className="font-mono font-medium text-gray-800">{fmt(data.totalKgProceso)} kg</span></p>
                      <p>Producto en Merma: <span className="font-mono font-medium text-red-600">{fmt(data.totalKgMerma)} kg</span></p>
                      <p>Producto Terminado: <span className="font-mono font-medium text-green-700">{fmt(data.totalKgProductoTerminadoNeto)} kg</span></p>
                    </div>
                    {data.mermaPorMotivo.length > 0 && (
                      <table className="w-full text-xs mt-2">
                        <tbody className="divide-y divide-gray-100">
                          {data.mermaPorMotivo.map((row) => (
                            <tr key={row.motivo}>
                              <td className="py-1 pl-4 text-gray-500">{row.motivo}</td>
                              <td className="py-1 text-right font-mono text-gray-700">{fmt(row.kgTotal)} kg</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              {/* KPI 1: Kg impresos por máquina (Flexo) */}
              {hasFlexoData && data.kgPorMaquinaFlexo.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart2 className="h-5 w-5 text-orange-500" />
                    <p className="text-sm font-semibold text-gray-700">KPI 1 — Kg impresos por máquina (Flexo)</p>
                  </div>
                  <div className="space-y-3">
                    {data.kgPorMaquinaFlexo.map((row) => (
                      <div key={row.machine}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-800">{row.machine}</span>
                          <span className="font-mono text-gray-600">{fmt(row.kgTotal)} kg</span>
                        </div>
                        <HBar value={row.kgTotal} max={maxKgFlexo} color="bg-orange-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* KPI 2: Cambios por máquina */}
              {hasFlexoData && data.cambiosPorMaquinaFlexo.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart2 className="h-5 w-5 text-indigo-500" />
                    <p className="text-sm font-semibold text-gray-700">KPI 2 — Cambios de trabajo por máquina (Flexo)</p>
                  </div>
                  <div className="space-y-3">
                    {data.cambiosPorMaquinaFlexo.map((row) => (
                      <div key={row.machine}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-800">{row.machine}</span>
                          <span className="font-mono text-gray-600">{fmtInt(row.cambios)} cambios</span>
                        </div>
                        <HBar value={row.cambios} max={maxCambios} color="bg-indigo-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No flexo data hint */}
              {!hasFlexoData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-2 text-sm text-blue-700">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>Sin datos de equipos Flexo (KPI 1, 2, 3). Activa "Toma Indicadores Flexo" en <strong>Catálogos → Equipos</strong> para las máquinas flexo.</p>
                </div>
              )}

              {/* Footer: total events */}
              <p className="text-xs text-gray-400 text-right">Basado en {fmtInt(data.eventsCount)} eventos de producción en el periodo.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
